import { useApiGetVersionHistoriesQuery } from '@seeqdev/api/endpoint/AuditApi';

export type ProductionVersionStatus = {
  /** Ids whose version history names them as its production version. A definitive yes. */
  inProductionItemIds: Set<string>;
  /**
   * Ids a readable version history came back for, whether or not it named them as production. An id asked about but
   * missing here is unknown, not confirmed-out-of-production: it may never have been versioned, or the caller may
   * not be entitled to its history. The API cannot tell those apart, because histories the caller cannot reach are
   * filtered out server-side rather than reported as forbidden.
   *
   * Callers must keep the two apart. Treating unknown as out-of-production warns about healthy items whenever the
   * caller's access is narrower than the item's history, which is routine for a read-only Organizer audience.
   */
  confirmedItemIds: Set<string>;
  /**
   * Names of the items a version history came back for, taken from the version's own item preview. Free of charge
   * from a response already in hand, but only covers confirmed ids — a name for any other id has to be looked up
   * separately.
   */
  itemNamesById: Record<string, string>;
  /**
   * Per requested id, whether that id's version history names any version as its production version. False means no
   * version of it is in production at all — either it was never promoted or it was withdrawn, which the data does
   * not distinguish and which need not be distinguished, since neither leaves anything to point at.
   */
  historyHasProductionVersionByItemId: Record<string, boolean>;
  isFetching: boolean;
  isLoading: boolean;
  /** False until a response has arrived, so callers can tell "nothing yet" from "nothing found". */
  hasResult: boolean;
  /** The args the query ran with, so callers can invalidate the same cache entry. */
  queryArgs: { itemIds: string[] };
};

/**
 * Asks, in a single request, which of `itemIds` are currently the production version of their version history.
 *
 * One request rather than one per id: the version-histories list endpoint is unpaginated, so fanning out over a
 * Topic's source Analyses would be costly. See SPEC/appserver/ITEM_VERSIONING.md §4.3.
 */
export const useProductionVersionStatus = (
  itemIds: string[],
  { enabled = true }: { enabled?: boolean } = {},
): ProductionVersionStatus => {
  // Sorted and de-duplicated so equivalent inputs produce the same structural React Query cache key.
  const queryArgs = { itemIds: [...new Set(itemIds)].sort() };
  const { isFetching, isLoading, data } = useApiGetVersionHistoriesQuery(queryArgs, {
    enabled: enabled && queryArgs.itemIds.length > 0,
  });

  const requested = new Set(queryArgs.itemIds);
  const inProductionItemIds = new Set<string>();
  const confirmedItemIds = new Set<string>();
  const itemNamesById: Record<string, string> = {};
  const historyHasProductionVersionByItemId: Record<string, boolean> = {};
  data?.versionHistories?.forEach((versionHistory) => {
    // Asked once per history rather than per item: whether a production version exists is a property of the
    // history. An unset productionVersionNumber matches no version, so this is false exactly when nothing is in
    // production.
    const historyHasProduction =
      versionHistory.versions?.some((version) => version?.version === versionHistory.productionVersionNumber) === true;
    versionHistory.versions?.forEach((version) => {
      const versionedItem = version?.versionedItem;
      if (!versionedItem?.id || !requested.has(versionedItem.id)) {
        return;
      }
      confirmedItemIds.add(versionedItem.id);
      historyHasProductionVersionByItemId[versionedItem.id] = historyHasProduction;
      if (versionedItem.name && !versionedItem.isRedacted) {
        itemNamesById[versionedItem.id] = versionedItem.name;
      }
      if (version.version === versionHistory.productionVersionNumber) {
        inProductionItemIds.add(versionedItem.id);
      }
    });
  });

  return {
    inProductionItemIds,
    confirmedItemIds,
    itemNamesById,
    historyHasProductionVersionByItemId,
    isFetching,
    isLoading,
    hasResult: data !== undefined,
    queryArgs,
  };
};
